import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Ship, Package, Wrench, ClipboardList, BookOpen,
  Wallet, FileText, Image as ImageIcon, BarChart3, UsersRound, Settings,
  LogOut, ChevronsUpDown, Anchor, Plus, Menu, X, ListChecks, Receipt, HelpCircle, Sparkles, ArrowRight,
  IdCard, UserSearch, Store, Briefcase, ShieldCheck, CalendarDays, MessageSquare, Search,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useYacht } from "../context/YachtContext";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Button } from "./ui/button";
import InvitationModal from "./InvitationModal";
import TransferModal from "./TransferModal";
import { GlobalSearch, openGlobalSearch } from "./GlobalSearch";
import WelcomeTourPrompt from "./tour/WelcomeTourPrompt";
import AssistantChat from "./assistant/AssistantChat";
import LanguageSwitcher from "./LanguageSwitcher";
import { useRights } from "../lib/useRights";
import { canSeeMarketplace } from "../lib/marketplaceAccess";
import { moduleForPath } from "../lib/modules";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";

// Sticky funnel banner shown only on the shareable read-only demo (viewer
// role). Turns every shared demo link into a soft signup prompt.
const DemoBanner = () => (
  <div
    data-testid="demo-banner"
    className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-ink/15 bg-ink px-4 py-2.5 text-white"
  >
    <div className="flex items-center gap-2 min-w-0">
      <Sparkles className="h-4 w-4 shrink-0 text-amber-300" />
      <p className="text-sm truncate">
        <span className="font-medium">You're exploring a read-only demo.</span>{" "}
        <span className="text-white/70">Create your own yacht to start managing for real.</span>
      </p>
    </div>
    <a
      href="https://seavanto.io"
      target="_blank"
      rel="noreferrer"
      data-testid="demo-banner-cta"
      className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-4 py-1.5 text-sm font-medium text-ink transition-colors hover:bg-amber-300"
    >
      Sign up free <ArrowRight className="h-3.5 w-3.5" />
    </a>
  </div>
);

// NAV is built inside the component so labels can pick up the active
// language. Order, icons and rights stay identical to the old constant.
function useNav() {
  const { t } = useTranslation();
  return [
    { to: "/app", label: t("nav.dashboard"), icon: LayoutDashboard, end: true, right: "dashboard:read" },
    { to: "/app/messages", label: t("nav.messages", "Messages"), icon: MessageSquare, right: null, module: "messages" },
    { to: "/app/yachts", label: t("nav.yachts"), icon: Ship, right: "yacht:read", module: "yachts" },
    { to: "/app/crew", label: t("nav.crew"), icon: UsersRound, right: "crew:read", module: "crew" },
    { to: "/app/talent", label: t("nav.talent_pool", "Talent pool"), icon: UserSearch, right: "crewhr:read", module: "talent" },
    { to: "/app/inventory", label: t("nav.inventory"), icon: Package, right: "inventory:read", module: "inventory" },
    { to: "/app/tasks", label: t("nav.tasks"), icon: ClipboardList, right: "maintenance:read", module: "tasks" },
    { to: "/app/calendar", label: t("nav.calendar", "Calendar"), icon: CalendarDays, right: "maintenance:read", module: "calendar" },
    { to: "/app/maintenance", label: t("nav.periodic_tasks"), icon: Wrench, right: "maintenance:read", module: "routines" },
    { to: "/app/checklists", label: t("nav.checklists"), icon: ListChecks, right: "checklist:read", module: "checklists" },
    { to: "/app/logbook", label: t("nav.logbook"), icon: BookOpen, right: "logbook:read", module: "logbook" },
    { to: "/app/petty-cash", label: t("nav.petty_cash"), icon: Wallet, right: "pettycash:read", module: "pettycash" },
    { to: "/app/invoices", label: t("nav.invoices"), icon: Receipt, right: "document:read", module: "invoices" },
    { to: "/app/documents", label: t("nav.documents"), icon: FileText, right: "document:read", module: "documents" },
    { to: "/app/photos", label: t("nav.photos"), icon: ImageIcon, right: "photo:read", module: "media" },
    { to: "/app/reports", label: t("nav.reports"), icon: BarChart3, right: "report:read", module: "reports" },
    { to: "/app/marketplace", label: t("nav.marketplace", "Marketplace"), icon: Store, right: "yacht:read", module: "marketplace" },
    { to: "/app/platform-admin", label: t("nav.platform_admin", "Platform Admin"), icon: ShieldCheck, right: null, adminOnly: true },
    { to: "/app/supplier", label: t("nav.supplier_hub", "Supplier hub"), icon: Briefcase, right: null, supplierOnly: true, end: true },
    { to: "/app/supplier/tasks", label: t("nav.supplier_tasks", "Tasks"), icon: ClipboardList, right: null, supplierOnly: true },
    { to: "/app/supplier/invoices", label: t("nav.supplier_invoices", "Invoices"), icon: Receipt, right: null, supplierOnly: true },
    { to: "/app/supplier/team", label: t("nav.supplier_team", "Team"), icon: UsersRound, right: null, supplierAdminOnly: true },
    { to: "/app/help", label: t("nav.help", "Help"), icon: HelpCircle, right: null },
    { to: "/app/my-hr", label: t("nav.my_hr", "My HR"), icon: IdCard, right: null },
    { to: "/app/profile", label: t("nav.my_profile", "My profile"), icon: IdCard, right: null },
  ];
}

// First-login gate for admin-created team accounts: the password the admin
// handed over must be replaced before using the app.
function ForcePasswordChange() {
  const { user, checkAuth, logout } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user?.must_change_password) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated — welcome aboard!");
      await checkAuth();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="font-display text-xl mb-1">Set your own password</h2>
        <p className="text-sm text-zinc-500 mb-5">
          Your account was created by your company admin. For security, choose a new password before continuing.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium" htmlFor="fpc-cur">Current password</label>
            <Input id="fpc-cur" type="password" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="The password you were given" data-testid="fpc-current-input" />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="fpc-new">New password</label>
            <Input id="fpc-new" type="password" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="At least 8 characters" data-testid="fpc-new-input" />
          </div>
          <Button type="submit" disabled={busy || !cur || nw.length < 8} className="w-full bg-ink hover:bg-black" data-testid="fpc-submit-btn">
            {busy ? "Saving…" : "Save new password"}
          </Button>
          <button type="button" onClick={logout} className="w-full text-center text-xs text-zinc-400 hover:text-zinc-600 underline underline-offset-2" data-testid="fpc-logout-btn">
            Not you? Sign out
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AppShell({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { yachts, activeYacht, switchYacht, refresh } = useYacht();
  const { can, role, moduleAllowed } = useRights();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const NAV = useNav();

  // Refresh the yacht list every time the user switches menu items inside
  // the app shell — covers the case where an approver finalised a deletion
  // (or a senior changed roles) on another device. Replaces the older 20s
  // interval poll which caused dialog-edits to flicker. Skip the very first
  // mount (YachtProvider already loaded on init) using a ref guard.
  const firstNavRef = useRef(true);
  useEffect(() => {
    if (firstNavRef.current) {
      firstNavRef.current = false;
      return;
    }
    refresh?.();
  }, [location.pathname, refresh]);

  // Direct-URL guard for per-member module blocks: navigating to a blocked
  // module's page bounces back to the dashboard.
  useEffect(() => {
    const mod = moduleForPath(location.pathname);
    if (mod && activeYacht && !moduleAllowed(mod)) nav("/app", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, activeYacht?.id, activeYacht?.my_blocked_modules]);

  // Per-user pending counts for the sidebar badges. Three independent
  // fetches: petty cash + maintenance are yacht-scoped (re-run on yacht
  // switch + route change), crew/invitations is user-scoped (re-run on
  // route change only — yacht switch can't affect it).
  // No timed polling — that's what kept biting us earlier. Approving a row
  // inside the Petty Cash page invalidates the cache by triggering a route
  // tick when the toast settles? Not quite — so we also refetch on
  // pathname changes which covers most workflows. If users need a sharper
  // refresh, they can also click the page.
  const [pcPending, setPcPending] = useState({ total: 0 });
  const [maintPending, setMaintPending] = useState({ total: 0 });
  const [crewPending, setCrewPending] = useState({ total: 0 });
  const [myHrPending, setMyHrPending] = useState({ total: 0 });
  const [supOpen, setSupOpen] = useState(0);
  const [supJobs, setSupJobs] = useState(0);
  const [supUnread, setSupUnread] = useState(0);
  const [supInvOpen, setSupInvOpen] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [invWaiting, setInvWaiting] = useState({ count: 0, outstanding: 0 });
  // Re-poll badge counts every 2 minutes so new messages / pending items
  // surface without a manual reload (data refresh only, no page reload).
  const [pollTick, setPollTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPollTick((t) => t + 1), 120000);
    const bump = () => setPollTick((t) => t + 1);
    window.addEventListener("seavanto:badges-refresh", bump);
    return () => { clearInterval(id); window.removeEventListener("seavanto:badges-refresh", bump); };
  }, []);
  useEffect(() => {
    let cancelled = false;
    const yid = activeYacht?.id;
    if (!yid) {
      setPcPending({ total: 0 });
      setMaintPending({ total: 0 });
      setMyHrPending({ total: 0 });
      setInvWaiting({ count: 0, outstanding: 0 });
    } else {
      (async () => {
        try {
          if (can("pettycash:read")) {
            const { data } = await api.get(`/petty-cash/my-pending?yacht_id=${yid}`);
            if (!cancelled) setPcPending(data);
          } else if (!cancelled) setPcPending({ total: 0 });
        } catch { if (!cancelled) setPcPending({ total: 0 }); }
        try {
          if (can("maintenance:read")) {
            const { data } = await api.get(`/maintenance/my-pending?yacht_id=${yid}`);
            if (!cancelled) setMaintPending(data);
          } else if (!cancelled) setMaintPending({ total: 0 });
        } catch { if (!cancelled) setMaintPending({ total: 0 }); }
        try {
          if (role !== "owner" && user?.account_kind !== "provider") {
            const { data } = await api.get(`/crew/me/payroll-pending?yacht_id=${yid}`);
            if (!cancelled) setMyHrPending(data);
          } else if (!cancelled) setMyHrPending({ total: 0 });
        } catch { if (!cancelled) setMyHrPending({ total: 0 }); }
        try {
          // Owner ask (2026-06): red badge on Invoices with the number of
          // invoices still waiting for payment.
          if (role === "owner" && can("document:read")) {
            const { data } = await api.get(`/invoices/summary/${yid}`);
            if (!cancelled) setInvWaiting({ count: data.by_status?.waiting || 0, outstanding: data.outstanding_amount || 0 });
          } else if (!cancelled) setInvWaiting({ count: 0, outstanding: 0 });
        } catch { if (!cancelled) setInvWaiting({ count: 0, outstanding: 0 }); }
      })();
    }
    (async () => {
      try {
        const { data } = await api.get(`/crew/my-pending`);
        if (!cancelled) setCrewPending(data);
      } catch { if (!cancelled) setCrewPending({ total: 0 }); }
      try {
        const { data } = await api.get(`/messages/unread`);
        if (!cancelled) setMsgUnread((data || []).reduce((s, i) => s + (i.unread || 0), 0));
      } catch { if (!cancelled) setMsgUnread(0); }
      if (user?.is_supplier) {
        try {
          const { data } = await api.get(`/supplier/open-count`);
          if (!cancelled) { setSupOpen(data.open_requests || 0); setSupJobs(data.open_jobs || 0); setSupUnread(data.unread_messages || 0); setSupInvOpen(data.open_invoices || 0); }
        } catch { if (!cancelled) { setSupOpen(0); setSupJobs(0); setSupUnread(0); setSupInvOpen(0); } }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeYacht?.id, location.pathname, pollTick]);

  // Service-provider accounts who haven't been added to ANY yacht yet
  // get a stripped-down sidebar: only Provider home + My profile. As soon
  // as a yacht owner adds them to a crew, the yacht-related items unlock
  // organically (yachts.length > 0 → standard nav).
  const isProviderUntethered = (
    user?.account_kind === "provider" && (yachts || []).length === 0
  );

  // Filter nav by per-yacht rights. When no yacht is selected, show everything
  // so the user can still navigate to Yachts/profile to set up access.
  const visibleNav = NAV.filter((n) => {
    if (isProviderUntethered) {
      // Provider with no yacht — only Dashboard (which renders the provider
      // landing), the Supplier hub (if linked) and My profile are reachable.
      return n.to === "/app" || n.to === "/app/profile" || n.to === "/app/messages" || ((n.to === "/app/supplier" || n.to === "/app/supplier/tasks" || n.to === "/app/supplier/invoices") && user?.is_supplier) || (n.to === "/app/supplier/team" && user?.is_supplier && user?.supplier_role === "admin");
    }
    if (n.supplierOnly && !user?.is_supplier) return false;
    if (n.supplierAdminOnly && !(user?.is_supplier && user?.supplier_role === "admin")) return false;
    if (n.adminOnly && !user?.is_admin) return false;
    // Online Suppliers (provider accounts) get an owner/crew-oriented Help
    // centre that isn't relevant to them — hide it. The read-only demo
    // (viewer) account also hides Help (its interactive tours don't apply).
    if (n.to === "/app/help" && (user?.account_kind === "provider" || user?.role === "viewer")) return false;
    // My HR (self-service employment record) — not relevant to provider
    // accounts, and owners aren't employees so they don't need it.
    if (n.to === "/app/my-hr" && (user?.account_kind === "provider" || role === "owner")) return false;
    // Talent pool — visible to admins and the allowed marketplace user.
    if (n.to === "/app/talent" && !(user?.is_admin || (user?.email || "").toLowerCase() === "faruk@yerli.cc")) return false;
    // Marketplace — visible to admins and the allowed marketplace user(s).
    if (n.to === "/app/marketplace" && !canSeeMarketplace(user)) return false;
    // Per-member module access (HR → Access tab): blocked modules disappear
    // from the menu on this yacht.
    if (n.module && activeYacht && !moduleAllowed(n.module)) return false;
    if (!n.right) return true;
    if (!activeYacht) return true;
    return can(n.right);
  });

  const initials = (user?.name || user?.email || "U").slice(0, 2).toUpperCase();
  const isDemoViewer = user?.role === "viewer";

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      {/* logo */}
      <div className="flex items-center gap-2 px-4 py-5 border-b border-zinc-200">
        <div className="w-7 h-7 rounded-md bg-ink text-white grid place-items-center">
          <Anchor className="h-4 w-4" />
        </div>
        <div className="font-display text-[17px] font-medium tracking-tight">Seavanto</div>
      </div>

      {/* yacht switcher */}
      <div className="px-3 pt-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="yacht-switcher"
              className="w-full flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
            >
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{t("nav.active_yacht")}</div>
                <div className="text-sm font-medium truncate">
                  {activeYacht?.name || t("nav.no_yacht_selected")}
                </div>
              </div>
              <ChevronsUpDown className="h-4 w-4 text-zinc-400 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>{user?.is_supplier ? t("nav.yachts") : t("nav.your_yachts")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {yachts.length === 0 && (
              <div className="px-2 py-3 text-sm text-zinc-500">{t("nav.no_yachts_yet")}</div>
            )}
            {yachts.map((y) => (
              <DropdownMenuItem
                key={y.id}
                onClick={() => switchYacht(y)}
                data-testid={`yacht-option-${y.id}`}
              >
                <Ship className="h-4 w-4 mr-2" />
                <div className="min-w-0">
                  <div className="text-sm">{y.name}</div>
                  <div className="text-xs text-zinc-500">{y.my_role === "provider" ? "Supplier" : (y.my_role || "")}</div>
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* global search trigger */}
      <div className="px-3 pt-2">
        <button
          data-testid="global-search-trigger"
          onClick={openGlobalSearch}
          className="w-full flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-500 hover:bg-zinc-50 transition-colors"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="truncate">{t("nav.search", "Search…")}</span>
          <kbd className="ml-auto hidden md:inline-flex items-center rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] text-zinc-400">⌘K</kbd>
        </button>
      </div>

      {/* nav */}
      <nav className="px-2 py-3 space-y-0.5 overflow-y-auto flex-1">
        {visibleNav.map((n) => {
          // The Petty Cash / Maintenance / Crew items get a small red badge
          // with the count of entries this user can act on. Hidden when the
          // total is 0 so the sidebar stays calm by default.
          let badge = null;
          let badgeTooltip = "";
          if (n.to === "/app/petty-cash" && pcPending.total > 0) {
            badge = pcPending.total;
            badgeTooltip = `${pcPending.awaiting_receipt || 0} top-up${(pcPending.awaiting_receipt || 0) === 1 ? "" : "s"} to acknowledge · ${pcPending.awaiting_approval || 0} expense${(pcPending.awaiting_approval || 0) === 1 ? "" : "s"} to approve`;
          } else if (n.to === "/app/tasks" && ((maintPending.ongoing_tasks || 0) + (maintPending.unread_messages || 0) + (maintPending.quotes_to_review || 0)) > 0) {
            badge = (maintPending.ongoing_tasks || 0) + (maintPending.unread_messages || 0) + (maintPending.quotes_to_review || 0);
            badgeTooltip = [
              (maintPending.ongoing_tasks || 0) > 0 ? `${maintPending.ongoing_tasks} ongoing task${maintPending.ongoing_tasks === 1 ? "" : "s"}` : "",
              (maintPending.quotes_to_review || 0) > 0 ? `${maintPending.quotes_to_review} quote${maintPending.quotes_to_review === 1 ? "" : "s"} to review` : "",
              (maintPending.unread_messages || 0) > 0 ? `${maintPending.unread_messages} unread supplier message${maintPending.unread_messages === 1 ? "" : "s"}` : "",
            ].filter(Boolean).join(" · ");
          } else if (n.to === "/app/maintenance" && (maintPending.overdue_triggered || 0) > 0) {
            badge = maintPending.overdue_triggered;
            badgeTooltip = `${maintPending.overdue_triggered} triggered task${maintPending.overdue_triggered === 1 ? "" : "s"} overdue — needs attention`;
          } else if (n.to === "/app/maintenance" && (maintPending.ongoing_triggered || 0) > 0) {
            badge = maintPending.ongoing_triggered;
            badgeTooltip = `${maintPending.ongoing_triggered} triggered job${maintPending.ongoing_triggered === 1 ? "" : "s"} ongoing (not yet confirmed)`;
          } else if (n.to === "/app/crew" && crewPending.total > 0) {
            badge = crewPending.total;
            badgeTooltip = `${crewPending.pending_invitations || 0} pending invitation${(crewPending.pending_invitations || 0) === 1 ? "" : "s"}`;
          } else if (n.to === "/app/my-hr" && myHrPending.total > 0) {
            badge = myHrPending.total;
            badgeTooltip = `${myHrPending.total} payroll entr${myHrPending.total === 1 ? "y" : "ies"} to confirm you received`;
          } else if (n.to === "/app/invoices" && invWaiting.count > 0) {
            badge = invWaiting.count;
            badgeTooltip = `${invWaiting.count} invoice${invWaiting.count === 1 ? "" : "s"} waiting for payment · ${invWaiting.outstanding.toFixed(2)} outstanding`;
          } else if (n.to === "/app/messages" && msgUnread > 0) {
            badge = msgUnread;
            badgeTooltip = `${msgUnread} unread message${msgUnread === 1 ? "" : "s"}`;
          } else if (n.to === "/app/supplier" && (supOpen + supUnread) > 0) {
            badge = supOpen + supUnread;
            badgeTooltip = [
              supOpen > 0 ? `${supOpen} new quote request${supOpen === 1 ? "" : "s"}` : "",
              supUnread > 0 ? `${supUnread} unread message${supUnread === 1 ? "" : "s"}` : "",
            ].filter(Boolean).join(" · ");
          } else if (n.to === "/app/supplier/tasks" && supJobs > 0) {
            badge = supJobs;
            badgeTooltip = `${supJobs} open job${supJobs === 1 ? "" : "s"} assigned to you`;
          } else if (n.to === "/app/supplier/invoices" && supInvOpen > 0) {
            badge = supInvOpen;
            badgeTooltip = `${supInvOpen} invoice${supInvOpen === 1 ? "" : "s"} not yet payment-confirmed`;
          }
          return (
            <NavLink
              key={n.to}
              to={n.to === "/app/my-hr" && myHrPending.total > 0 ? "/app/my-hr?tab=payroll" : n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-white border border-zinc-200 text-ink font-medium"
                    : "text-zinc-600 hover:bg-white/70 hover:text-ink"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              <span className="flex-1">{n.label}</span>
              {badge != null && (
                <span
                  className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-medium bg-red-600 text-white"
                  data-testid={
                    n.to === "/app/petty-cash" ? "pc-sidebar-badge" :
                    n.to === "/app/tasks" ? "tasks-sidebar-badge" :
                    n.to === "/app/maintenance" ? "maint-sidebar-badge" :
                    n.to === "/app/crew" ? "crew-sidebar-badge" :
                    n.to === "/app/my-hr" ? "myhr-sidebar-badge" :
                    n.to === "/app/messages" ? "messages-sidebar-badge" :
                    n.to === "/app/invoices" ? "invoices-sidebar-badge" :
                    n.to === "/app/supplier" ? "supplier-sidebar-badge" :
                    n.to === "/app/supplier/tasks" ? "supplier-tasks-sidebar-badge" : undefined
                  }
                  title={badgeTooltip}
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* user + language */}
      <div className="border-t border-zinc-200 p-2 space-y-1">
        <LanguageSwitcher />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu" className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-white transition-colors">
              <Avatar className="h-7 w-7">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="text-xs bg-ink text-white">{initials}</AvatarFallback>
              </Avatar>
              <div className="text-left min-w-0">
                <div className="text-sm truncate">{user?.name || user?.email}</div>
                <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="settings-menu">
              <Settings className="h-4 w-4 mr-2" /> {t("nav.my_profile", "My profile")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={doLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> {t("nav.sign_out")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-zinc-50">
      <ForcePasswordChange />
      {/* sidebar desktop */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-zinc-200 bg-[#F4F4F5]">
        <SidebarInner />
      </aside>

      {/* mobile sheet */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-[#F4F4F5] border-r border-zinc-200">
            <SidebarInner />
          </aside>
        </div>
      )}

      {/* content */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* mobile topbar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-white">
          <button data-testid="open-sidebar" onClick={() => setOpen(true)} className="p-2 -m-2">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/app" className="font-display font-medium">Seavanto</Link>
          <button data-testid="global-search-trigger-mobile" onClick={openGlobalSearch} className="p-2 -m-2">
            <Search className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 min-w-0 pb-24 md:pb-0">
          {isDemoViewer && <DemoBanner />}
          {children}
        </div>
      </main>
      <InvitationModal />
      <TransferModal />
      <GlobalSearch />
      <WelcomeTourPrompt />
      <AssistantChat />
    </div>
  );
}
